import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxSpinnerService } from 'ngx-spinner';
import { NodeTypes, SlurmPartitionAdditionalPropsDescription, SlurmPartitionDefaultValues } from '../../../../core/models/globals';
import { DeploymentService } from '../../../../core/services/deployment.service';
import { NodeService } from '../../../../core/services/node.service';
import { Node } from 'src/app/core/models/node';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-update-node-group',
  templateUrl: './update-node-group.component.html',
  styleUrls: ['./update-node-group.component.scss']
})
export class UpdateNodeGroupComponent implements OnInit {
  @ViewChild('multiSelect') multiSelect: MatSelect;

  constructor(
    private deploymentService: DeploymentService,
    public dialogRef: MatDialogRef<UpdateNodeGroupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      action: string, 
      depName: string, 
      nodeGroups: string,
      isVdi: boolean,
      alredayAssignedNodes: string
    },
    private _spinner: NgxSpinnerService,
    private snackBar: MatSnackBar,
    private nodeService: NodeService
  ) {
    this.dictionaryKeys = Object.keys(this.dictionary);
  }

  dictionary: { [key: string]: any } = {
    ...Object.keys(SlurmPartitionDefaultValues).filter(key => SlurmPartitionDefaultValues[key]).reduce((acc, key) => {
      acc[key] = SlurmPartitionDefaultValues[key];
      return acc;
    }, {} as { [key: string]: SlurmPartitionDefaultValues }),
  } as const;

  constantNonChangeableDict: { [key: string]: any } = {
    ...Object.keys(SlurmPartitionDefaultValues).filter(key => SlurmPartitionDefaultValues[key]).reduce((acc, key) => {
      acc[key] = SlurmPartitionDefaultValues[key];
      return acc;
    }, {} as { [key: string]: SlurmPartitionDefaultValues }),
  } as const;

  selectedProperty: string = '';
  selectedValue: string = '';
  dictionaryKeys;
  updateNodeGroup: UntypedFormGroup;
  deleteNodeGroup: UntypedFormGroup;
  computeNodes: string[] = [];
  selectedComputeNodes: string[] = [];
  setAdditionalProperties: boolean = false;
  additionalPropertiesSetDictionary: { [key: string]: any } = {};
  existingNodeGroupsList: string[] = [];
  isCreate: boolean = false;
  isUpdate: boolean = false;
  isDelete: boolean = false;
  nodeGroupSelectedToDelete: string = "";
  nodeGroupSelectedToDeleteText: string = "";
  nodesData: Node[] | null = null;
  showAdditionalSettings: boolean = !this.data.isVdi
  selectNodeMatLabel: string = "Select Compute Nodes";
  filteredComputeNodes: string[] = [];
  multiFilterCtrl: FormControl = new FormControl();
  filteredMulti: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  _onDestroy = new Subject<void>();
  disableSubmit: boolean = true;

  additionalPropInfoDictionary: { [key: string]: any } = {
    ...Object.keys(SlurmPartitionAdditionalPropsDescription).filter(key => SlurmPartitionAdditionalPropsDescription[key]).reduce((acc, key) => {
      acc[key] = SlurmPartitionAdditionalPropsDescription[key];
      return acc;
    }, {} as { [key: string]: SlurmPartitionAdditionalPropsDescription }),
  } as const;

  ngOnInit(): void {
    if(this.data.isVdi == undefined || this.data.isVdi == false){
       this.snackBar.open("This action will affect the running jobs since it will restart the Slurm controller service.", "Ok").afterDismissed().subscribe(() => {
        this.disableSubmit = false;
      });
    }
    else{
      this.disableSubmit = false;
    }
    if (this.data.action?.toLowerCase() == "create") this.isCreate = true;
    if (this.data.action?.toLowerCase() == "update") this.isUpdate = true;
    if (this.data.action?.toLowerCase() == "delete") this.isDelete = true;

    if(this.data.isVdi) { this.selectNodeMatLabel = "Select workstations" }

    this.getAllComputeNodesForDep();
    if (this.data?.nodeGroups) {
      this.existingNodeGroupsList = this.data.nodeGroups?.split(',')?.map(node => node?.trim());
      this.filteredComputeNodes = [...this.computeNodes];
    }
    this.updateNodeGroup = new UntypedFormGroup({
      nodeGroupName: new UntypedFormControl(
        '',
        [Validators.required, Validators.pattern('^[A-Za-z0-9-_.@]+$')]
      ),
      nodes: new UntypedFormControl(null, Validators.required),
    });
    this.deleteNodeGroup = new UntypedFormGroup({
      nodeGroupName: new UntypedFormControl(this.data.nodeGroups, Validators.required),
      confirmText: new UntypedFormControl(null, Validators.required)
    });

    if(this.isUpdate){
      this.updateNodeGroup.controls["nodeGroupName"].setValue(this.data.nodeGroups);
    }
    else if(this.isDelete){
      this.deleteNodeGroup.controls["nodeGroupName"].setValue(this.data.nodeGroups);
      this.nodeGroupSelectedToDelete = this.data.nodeGroups;
    }
    this.multiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterOptions();
      });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onSubmit(form) {
     this._spinner.show('updateCredential')
    try {
      if (form.status == 'INVALID') {
        this.updateNodeGroup.markAllAsTouched();
        this._spinner.hide('updateCredential')
        this.snackBar.open("Please verify all form fields are entered and entered correctly", "Ok", { duration: 3000 });
        return;
      }

      if (this.isCreate || this.isUpdate) {
        if (form.value?.nodeGroupName) {
          if (this.existingNodeGroupsList?.includes(form.value?.nodeGroupName) && !this.isUpdate) {
            this.updateNodeGroup.markAllAsTouched();
            this._spinner.hide('updateCredential')
            this.snackBar.open("Node group (or partition) with entered name already exists", "Ok", { duration: 3000 });
            return;
          }
        }

        if (this.setAdditionalProperties) {
          this.additionalPropertiesSetDictionary = {};

          for (const key in this.dictionary) {
            if (
              this.dictionary.hasOwnProperty(key) &&
              this.dictionary[key] !== this.constantNonChangeableDict[key]
            ) {
              this.additionalPropertiesSetDictionary[key] = this.dictionary[key];
            }
          }
        }
      }

      let updateNodeGroupValues: any = {
        partitionName: form.value?.nodeGroupName,
        computeNodes: form.value?.nodes?.toString() ? form.value?.nodes?.toString() : "NA",
        additionalProperties: this.additionalPropertiesSetDictionary
      };

      this.deploymentService.updatePartition(
        this.data.depName,
        "slurm",
        this.setAdditionalProperties?.toString(),
        updateNodeGroupValues,
        this.data?.action,
        this.data.isVdi
      ).subscribe({
        next: (data: any) => {
          //this.snackBar.open(data?.actionMessage, "Ok", { duration:3000 });
          this._spinner.hide('updateCredential');
          this.dialogRef.close(data?.actionMessage);
        },
        error: (error: any) => {
          //this.snackBar.open(error?.error?.actionMessage, "Ok", { duration: 3000 });
          this._spinner.hide('updateCredential')
          this.dialogRef.close(error?.error?.actionMessage);
        }
      });
    }
    catch (e) { }
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  updateDictionary() {
    if (this.selectedProperty && this.selectedValue) {
      this.dictionary[this.selectedProperty] = this.selectedValue;
    }
  }

  checkSelectedCheckboxes(event) {
    if (event.target.checked) {
      this.setAdditionalProperties = true;
    }
    else {
      this.setAdditionalProperties = false;
    }
  }

  getAllComputeNodesForDep() {
    this._spinner.show('updateCredential')
    this.nodeService.getNodesForDeployment(this.data.depName).subscribe((data) => {
      if (!data) {
        this._spinner.hide('updateCredential')
        this.snackBar.open("Unable to fetch compute nodes.", "Ok", { duration: 5000 });
        return;
      }
      this.nodesData = <Node[]>JSON.parse(JSON.stringify(data));
      if (this.nodesData) {
        if(this.data.isVdi){
          this.computeNodes = this.nodesData.filter(n => !n.nodeType.toLowerCase().includes(NodeTypes.TxtHwHost.toLowerCase())).map(vmName => vmName.vmName);
          this.filteredComputeNodes = [...this.computeNodes];
        }
        else{
          this.computeNodes = this.nodesData.filter(n => n.nodeType.toLowerCase().includes("compute")).map(vmName => vmName.vmName);
          this.filteredComputeNodes = [...this.computeNodes];
        }
      }
    });
    this._spinner.hide('updateCredential');
  }

  getOptionTitle(key: string): void {
    return this.additionalPropInfoDictionary[key];
  }

  getComputeNodesForPartition() {
    if (this.nodesData && this.updateNodeGroup.value?.nodeGroupName != "" && this.updateNodeGroup.value?.nodeGroupName != null) {
      if(this.data.isVdi){
        this.nodesData = this.nodesData.filter(n => !n.nodeType.toLowerCase().includes(NodeTypes.TxtHwHost.toLowerCase()));
      }
      else{
        this.nodesData = this.nodesData.filter(n => n.nodeType.toLowerCase().includes("compute"));
      }
      
      const partitionNames = this.nodesData
        .filter(({ azureServiceName, vmName }) => {
          const names = azureServiceName?.split(',') ?? [];
          return (
            names.length > 0 &&
            names.includes(this.updateNodeGroup.value?.nodeGroupName)
          );
        })
        .map(({ vmName }) => vmName);
      if(partitionNames.length > 0 && !this.updateNodeGroup.value.nodes){
        this.updateNodeGroup.controls['nodes'].setValue(partitionNames);
      }
    }
    if(this.data.isVdi){
      if(this.nodesData) {
        this.nodesData = this.nodesData.filter(n => !n.nodeType.toLowerCase().includes(NodeTypes.TxtHwHost.toLowerCase()));
      }
    }
    else{
      if(this.nodesData) {
        this.nodesData = this.nodesData.filter(n => n.nodeType.toLowerCase().includes("compute"));
      }
    }
    return this.nodesData?.map(vm => vm.vmName) || [];
  }

  filterOptions() {
    if (!this.computeNodes) {
      return;
    }
    let search = this.multiFilterCtrl.value;
    if (!search) {
      this.filteredComputeNodes = this.computeNodes.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredComputeNodes = this.computeNodes.filter(x => x.toLowerCase().indexOf(search) > -1);
  }

  setInitialValue() {
    this.filteredMulti
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        if(this.isUpdate) {
          this.updateNodeGroup.controls['nodes'].setValue(this.selectedComputeNodes);
        }
        // this.multiSelect.compareWith = (a: Bank, b: Bank) => a && b && a.id === b.id;
      });
  }

  optionSelected(vmName) {
    if(vmName) {
      if(this.computeNodes) {
        this.selectedComputeNodes.push(vmName);
      }
    }
  }
}
